import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { BarterWarning } from '../barter/BarterWarning';
import { ConflictNotice } from '../ui/ConflictNotice';
import { ClientField, DaysFields, EquipmentSourceField, MoreTermsField, ReserveAroundField, type Days, QuoteSwitch, ReservationField, ResponsibleField, ScopePicker, useCustomScopes } from './EventFields';
import { SystemChoice } from '../settings/SystemChoice';
import { PlaceField } from './PlaceField';
import { autoRouteHint } from './defaultRoute';
import { parseRouteKm, placeCreateFields, placeEditFields, routeKmText, visiblePlace, type ChosenRoute } from './place';
import { useDefaultSystem } from '../settings/systems';
import { datesToInstants, DEFAULT_EQUIPMENT_SOURCE, EVENT_KINDS, EVENTS_RESOURCE, instantsToDates, runsOf, SETTLEMENT_LABELS, type EventKind, type EventSegment, type EventView, type ReservationKind, type Settlement } from './labels';
import { refreshEvents } from './refresh';
import { DraftNotice, useFormDraft } from '../nav/drafts';
import { useCalendarLink, type CalendarLink } from './calendarLink';
import { pickOfTerms, termsOfPick } from './pickDates';
import { leaveWithoutAsking, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { ViewersFormField } from './ViewersField';
import { maySeeScopeLines, readScopeLines, scopeLinesNotice } from './scopeLines';
import { autoPlaceHint, fillIfUnchanged, freshPlaceOf, isAutoFilled, placeOfDraft, useAutoPlace } from './autoPlace';
import { offerPlaceRule } from './RememberRule';

type Picked = { id: string; name: string };

export type Draft = {
  name: string;
  firstDay: string;
  firstTime: string;
  lastDay: string;
  lastTime: string;
  client: Picked | null;
  location: string;
  locationAddress: string;
  locationPoint: { lat: number; lng: number } | null;
  locationApprox: boolean;
  /** "km wg trasy" as typed; '' = count the distance. */
  routeKm: string;
  /** Spec 2026-10-22-mapy-google: the place in "Miejsca wydarzeń", its Google id and notes, the route (optional: older drafts). */
  venueId?: string | null | undefined;
  googlePlaceId?: string | undefined;
  placeNotes?: string | undefined;
  avoidTolls?: boolean | undefined;
  chosenRoute?: ChosenRoute | null | undefined;
  /** Where the place of the ids is (the form's memory, never sent; review I4). */
  placePoint?: { lat: number; lng: number } | null | undefined;
  reservationKind: ReservationKind;
  /** "Rezerwuj sprzęt także: N dni przed / po" (owner 06:47); optional so older drafts still load. */
  reserveDaysBefore?: number;
  reserveDaysAfter?: number;
  /** F28: an event we play or a rental of gear to another company. */
  kind: EventKind;
  systemId: string;
  equipmentSource: string;
  scopes: string[];
  notes: string;
  needsQuote: boolean;
  /** „Rozliczenie” (owner 2026-11-03): 'faktura' unless set; optional so older drafts still load. */
  settlement?: Settlement | undefined;
  /** "Odpowiedzialny" (P16, owner point 3): '' = nobody. */
  managerUserId: string;
  /** The second and further terms (owner 2026-10-21 00:44: one event of several terms); none = one term, the fields above. */
  moreTerms?: Days[];
  /**
   * "Kto widzi" (spec 2026-10-23-widocznosc-wydarzen), only when adding (later: the ⚙ of "Widzą" in "Szczegóły");
   * undefined = untouched (the server lists the creator alone).
   */
  viewerIds?: string[] | undefined;
};

/** Every term of the draft: the day fields first, then the further terms. */
const allTerms = (d: Draft): EventSegment[] => [datesToInstants(d), ...(d.moreTerms ?? []).map(datesToInstants)];

/** A new event from days picked with Shift / Cmd: the first run of days in the day fields, the others as further terms. */
export function initialTermsDraft(days: readonly string[]): Pick<Draft, 'firstDay' | 'lastDay' | 'firstTime' | 'lastTime' | 'moreTerms'> {
  const [first, ...rest] = runsOf(days);
  return {
    firstDay: first?.first ?? '',
    lastDay: first?.last ?? '',
    firstTime: '',
    lastTime: '',
    moreTerms: rest.map((r) => ({ firstDay: r.first, lastDay: r.last, firstTime: '', lastTime: '' })),
  };
}

/**
 * What an edit sends for the dates: nothing when they did not change; the terms (`segments`) for an event that had
 * several or gets several (one left is sent as one term); start and end for a plain event, as before.
 */
export function termsPatch(hadTerms: boolean, original: Draft, draft: Draft): { startsAt?: string; endsAt?: string; segments?: EventSegment[] } {
  const keys = ['firstDay', 'firstTime', 'lastDay', 'lastTime'] as const;
  const changed = keys.some((k) => draft[k] !== original[k]) || JSON.stringify(draft.moreTerms ?? []) !== JSON.stringify(original.moreTerms ?? []);
  if (!changed) return {};
  if (hadTerms || (draft.moreTerms?.length ?? 0) > 0) return { segments: allTerms(draft) };
  return datesToInstants(draft);
}

/** The start of the draft's first term (for the traffic of "Pokaż trasę"), null while the day is not a day. */
function startOf(d: Draft): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.firstDay)) return null;
  try {
    return datesToInstants({ ...d, lastDay: d.lastDay || d.firstDay }).startsAt;
  } catch {
    return null;
  }
}

/** "km wg trasy" of the draft as a number; null when empty (or not a number — the field shows that, and saving waits). */
const routeKmOf = (draft: Pick<Draft, 'routeKm'>): number | null => {
  const r = parseRouteKm(draft.routeKm);
  return r.ok ? r.value : null;
};

/** Add: only `name`, the dates and fields that differ from the defaults, so a role that may not write some field can still add an event. */
export function createFields(draft: Draft, visible: (field: string) => boolean = () => true): Record<string, unknown> {
  return {
    ...placeCreateFields(draft, visible),
    name: draft.name,
    // several terms (owner 2026-10-21 00:44): `segments` instead of start and end
    ...((draft.moreTerms?.length ?? 0) > 0 ? { segments: allTerms(draft) } : datesToInstants(draft)),
    ...(draft.client ? { contractorId: draft.client.id } : {}),
    ...(draft.location !== '' ? { location: draft.location } : {}),
    ...(draft.locationAddress !== '' ? { locationAddress: draft.locationAddress } : {}),
    ...(draft.locationPoint ? { locationPoint: draft.locationPoint } : {}),
    ...(!draft.locationApprox ? { locationApprox: false } : {}),
    ...(routeKmOf(draft) !== null ? { routeKm: routeKmOf(draft) } : {}),
    ...(draft.reservationKind !== 'soft' ? { reservationKind: draft.reservationKind } : {}),
    ...(draft.reserveDaysBefore ? { reserveDaysBefore: draft.reserveDaysBefore } : {}),
    ...(draft.reserveDaysAfter ? { reserveDaysAfter: draft.reserveDaysAfter } : {}),
    ...(draft.kind !== 'impreza' ? { kind: draft.kind } : {}),
    ...(draft.systemId !== '' ? { systemId: draft.systemId } : {}),
    ...(draft.equipmentSource !== '' ? { equipmentSource: draft.equipmentSource } : {}),
    ...(draft.scopes.length > 0 ? { scopes: draft.scopes } : {}),
    ...(draft.notes !== '' ? { notes: draft.notes } : {}),
    ...(draft.needsQuote ? { needsQuote: true } : {}),
    ...(draft.settlement && draft.settlement !== 'faktura' && visible('settlement') ? { settlement: draft.settlement } : {}),
    ...(draft.managerUserId !== '' ? { managerUserId: draft.managerUserId } : {}),
    ...(draft.viewerIds && visible('viewerIds') ? { viewerIds: draft.viewerIds } : {}),
  };
}

const draftOf = (e: EventView | undefined, clientName: string): Draft => ({
  name: e?.name ?? '',
  ...(e ? (e.segments && e.segments.length > 1 ? instantsToDates(e.segments[0]!.startsAt, e.segments[0]!.endsAt) : instantsToDates(e.startsAt, e.endsAt)) : { firstDay: '', firstTime: '', lastDay: '', lastTime: '' }),
  ...(e?.segments && e.segments.length > 1 ? { moreTerms: e.segments.slice(1).map((t) => instantsToDates(t.startsAt, t.endsAt)) } : {}),
  client: e?.contractorId ? { id: e.contractorId, name: clientName } : null,
  location: e?.location ?? '',
  locationAddress: e?.locationAddress ?? '',
  locationPoint: e?.locationPoint ?? null,
  locationApprox: e?.locationApprox ?? true,
  routeKm: routeKmText(e?.routeKm),
  venueId: e?.venueId ?? null,
  googlePlaceId: e?.googlePlaceId ?? '',
  placeNotes: e?.placeNotes ?? '',
  // where the place of the ids is: the place of "Miejsca wydarzeń", else the event's point (review I4)
  placePoint: e?.placePoint ?? (e?.googlePlaceId || e?.venueId ? (e?.locationPoint ?? null) : null),
  avoidTolls: e?.avoidTolls ?? false,
  chosenRoute: e?.chosenRoute ?? null,
  reservationKind: e?.reservationKind ?? 'soft',
  reserveDaysBefore: e?.reserveDaysBefore ?? 0,
  reserveDaysAfter: e?.reserveDaysAfter ?? 0,
  kind: e?.kind ?? 'impreza',
  systemId: e?.systemId ?? '',
  equipmentSource: e?.equipmentSource ?? '',
  // a new event starts with "Dźwięk kompletny" (owner remark 4)
  scopes: e ? (e.scopes ?? []) : ['dzwiek'],
  notes: e?.notes ?? '',
  needsQuote: e?.needsQuote ?? false,
  settlement: e?.settlement ?? 'faktura',
  managerUserId: e?.managerUserId ?? '',
});

/** The form's values of an event (edit). */
export const draftOfEvent = draftOf;

/**
 * A new event: "Dźwięk kompletny" (owner remark 4), the default system once the list loads (3/13, `useDefaultSystem`), "Potrzebna wycena" on (3/14) and gear from our warehouse (3/15) —
 * the last two only when the role may write the field (otherwise nothing is sent for it).
 */
export const newDraft = (visible: (field: string) => boolean): Draft => ({
  ...draftOf(undefined, ''),
  equipmentSource: visible('equipmentSource') ? DEFAULT_EQUIPMENT_SOURCE : '',
  needsQuote: visible('needsQuote'),
});

function issue(error: unknown, path: string): string | undefined {
  return error instanceof ApiError ? fieldIssues(error).find((i) => i.path === path)?.message : undefined;
}

function FieldError({ error, path }: { error: unknown; path: string }) {
  if (!(error instanceof ApiError)) return null;
  const issue = fieldIssues(error).find((i) => i.path === path);
  return issue ? <span className="field-error">{issue.message}</span> : null;
}

/**
 * Add (no `event`) or edit. On edit only changed fields go out (rule 7a), and a field the server did not send is neither shown nor sent.
 * Dates: first/last day plus optional hours, turned into Warsaw-time instants in `datesToInstants`.
 */
export function EventForm({
  event,
  clientName = '',
  onDone,
  onSaved,
  onQuoteNotice,
  initialDay,
  initialDays,
  calendar,
}: {
  event?: EventView;
  clientName?: string;
  onDone: () => void;
  onSaved?: ((saved: EventView) => void) | undefined;
  /** Owner 2026-10-30: what saving the scope did to the event's quote (lines added, taken off, kept), for the card to say. */
  onQuoteNotice?: ((text: string) => void) | undefined;
  /** A new event on this day (P6: a double click on a day of the calendar). */
  initialDay?: string | undefined;
  /** A new event of these days (owner 2026-10-21 00:44: picked with Shift / Cmd): each run of days one term. */
  initialDays?: string[] | undefined;
  /** A new event: the calendar beside the panel picks the terms' days (owner 2026-10-22 14:40); hours and the rest stay. */
  calendar?: CalendarLink | undefined;
}) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(event);
  const [baseClient, setBaseClient] = useState(clientName);
  const { me } = useMe();
  // P13: what was typed waits for the way back (per event; a new one per chosen day)
  const kept = useFormDraft<Draft>(event ? `events.event:${event.id}` : `events.event:new:${initialDays?.join(',') ?? initialDay ?? ''}`, base?.version ?? 0);
  // how a new event's form opened: anything else is a change (owner 2026-10-23 13:34), also dates from the calendar
  const [fresh] = useState<Draft>(() =>
    event
      ? draftOf(event, clientName)
      : { ...newDraft((f) => !me || fieldVisible(me, EVENTS_RESOURCE, f)), ...(initialDays && initialDays.length > 0 ? initialTermsDraft(initialDays) : initialDay ? { firstDay: initialDay, lastDay: initialDay } : {}) },
  );
  const [draft, setDraft] = useState(() => kept.initial ?? fresh);
  /** The system set by itself once the list loads (not a change of the person). */
  const autoSystem = useRef<string | null>(null);
  kept.track(draft);
  useCalendarLink(event ? undefined : calendar, pickOfTerms(draft), (days) => setDraft((d) => ({ ...d, ...termsOfPick(days, d) })));
  /** Saved or cancelled: the draft is no longer wanted. */
  const finish = () => {
    kept.clear();
    onDone();
  };
  const scopes = useCustomScopes(!!me && can(me, 'events.scopes', 'create'));
  const visible = (field: string) => !base || field in base;
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  // a new event takes the default system once the list loads (never a fixed id that may have been deleted)
  useDefaultSystem(
    draft.systemId,
    (systemId) => {
      autoSystem.current = systemId;
      set({ systemId });
    },
    !base && !!me && fieldVisible(me, EVENTS_RESOURCE, 'systemId'),
  );
  // owner 2026-10-30 22:20 / 22:23: a new event comes with the place of a rule in its name, else the default place
  const placeShown = !!me && ['location', 'locationAddress', 'locationPoint', 'locationApprox'].every((f) => fieldVisible(me, EVENTS_RESOURCE, f));
  const autoPlace = useAutoPlace(draft.name, placeOfDraft(draft), (p, from) => setDraft((d) => fillIfUnchanged(d, from, p)), !base && placeShown);

  /** What an edit sends: only the changed fields the role sees (rule 7a). */
  function editFields(base: EventView) {
    const original = draftOf(base, baseClient);
    const changed = (key: keyof Draft) => JSON.stringify(draft[key]) !== JSON.stringify(original[key]);
    const wanted = (key: keyof Draft, field: string) => visible(field) && changed(key);
    const fields = {
      ...(wanted('name', 'name') ? { name: draft.name } : {}),
      // one term: start and end; several (owner 2026-10-21 00:44): the terms
      ...termsPatch(!!base.segments && base.segments.length > 1, original, draft),
      ...(wanted('client', 'contractorId') && true ? { contractorId: draft.client?.id ?? null } : {}),
      ...(wanted('location', 'location') ? { location: draft.location } : {}),
      ...(wanted('locationAddress', 'locationAddress') ? { locationAddress: draft.locationAddress } : {}),
      ...(wanted('locationPoint', 'locationPoint') ? { locationPoint: draft.locationPoint } : {}),
      ...(wanted('locationApprox', 'locationApprox') ? { locationApprox: draft.locationApprox } : {}),
      ...(wanted('routeKm', 'routeKm') && routeKmOf(draft) !== base.routeKm ? { routeKm: routeKmOf(draft) } : {}),
      // spec 2026-10-22-mapy-google: the place's ids, "Unikaj płatnych", the chosen route (rule 7a: visible and changed)
      ...placeEditFields(original, draft, (f) => (f === 'tollsGrosze' ? !me || fieldVisible(me, EVENTS_RESOURCE, f) : visible(f))),
      ...(wanted('reservationKind', 'reservationKind') ? { reservationKind: draft.reservationKind } : {}),
      ...(wanted('reserveDaysBefore', 'reserveDaysBefore') ? { reserveDaysBefore: draft.reserveDaysBefore ?? 0 } : {}),
      ...(wanted('reserveDaysAfter', 'reserveDaysAfter') ? { reserveDaysAfter: draft.reserveDaysAfter ?? 0 } : {}),
      ...(wanted('kind', 'kind') ? { kind: draft.kind } : {}),
      ...(wanted('systemId', 'systemId') ? { systemId: draft.systemId || null } : {}),
      ...(wanted('equipmentSource', 'equipmentSource') ? { equipmentSource: draft.equipmentSource } : {}),
      ...(wanted('scopes', 'scopes') ? { scopes: draft.scopes } : {}),
      ...(wanted('notes', 'notes') ? { notes: draft.notes } : {}),
      ...(wanted('needsQuote', 'needsQuote') ? { needsQuote: draft.needsQuote } : {}),
      ...(wanted('settlement', 'settlement') ? { settlement: draft.settlement ?? 'faktura' } : {}),
      ...(wanted('managerUserId', 'managerUserId') ? { managerUserId: draft.managerUserId || null } : {}),
    };
    return { original, fields };
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!base) return api<EventView>('/api/events', { method: 'POST', body: { ...createFields(draft, (f) => !me || fieldVisible(me, EVENTS_RESOURCE, f)), ...autoRouteHint(queryClient, draft) } });
      const { original, fields } = editFields(base);
      // owner 2026-10-23 13:34: the route previewed for the new point, so the server takes it instead of asking Google
      const hint = 'locationPoint' in fields || 'avoidTolls' in fields ? autoRouteHint(queryClient, draft, original) : {};
      // owner 2026-10-30: the scope's lines in the quote before and after (only for whoever may edit quotes)
      const tell = 'scopes' in fields && !!onQuoteNotice && maySeeScopeLines(me);
      const before = tell ? await readScopeLines(base.id, false) : null;
      const saved = await api<EventView>(`/api/events/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields, ...hint } });
      const after = before ? await readScopeLines(base.id, true) : null;
      const text = before && after ? scopeLinesNotice(before, after) : '';
      if (text) onQuoteNotice?.(text);
      return saved;
    },
    onSuccess: async (saved) => {
      await refreshEvents(queryClient);
      // a place of its own: „Zapamiętać: „PFK” → …?” (owner 2026-10-30 22:23)
      if (!base) offerPlaceRule(me, draft.name, placeOfDraft(draft), autoPlace.state, autoPlace.rules);
      onSaved?.(saved);
      finish();
    },
  });

  // owner 2026-10-23 13:34: closing with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const dirty = base
    ? Object.keys(editFields(base).fields).length > 0 || !parseRouteKm(draft.routeKm).ok
    : !sameValues({ ...draft, systemId: draft.systemId === autoSystem.current ? fresh.systemId : draft.systemId, ...(isAutoFilled(autoPlace.state, placeOfDraft(draft)) ? freshPlaceOf(fresh) : {}) }, fresh);
  const guardRef = useUnsavedGuard({
    dirty,
    save: async () => {
      if (!parseRouteKm(draft.routeKm).ok) return false;
      await save.mutateAsync();
      return true;
    },
    discard: () => kept.clear(),
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  async function reload() {
    const current = (save.error as ApiError).current as EventView | undefined;
    const fresh = current ?? (await api<EventView>(`/api/events/${base!.id}`));
    let name = '';
    if (fresh.contractorId) {
      try {
        name = (await api<Picked>(`/api/contractors/${fresh.contractorId}`)).name;
      } catch {
        // the user may not see contractors; the id is still kept
      }
    }
    setBase(fresh);
    setBaseClient(name);
    setDraft(draftOf(fresh, name));
    save.reset();
    await refreshEvents(queryClient);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!parseRouteKm(draft.routeKm).ok) return; // the field says what is wrong
    save.mutate();
  }

  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <form ref={guardRef} className={base ? 'edit' : 'panel form'} aria-label={base ? `Edycja: ${base.name}` : 'Nowe wydarzenie'} data-feature-id="events.edit" onSubmit={submit}>
      {!base && <h2 tabIndex={-1}>Nowe wydarzenie</h2>}
      <DraftNotice keeper={kept} onRestore={setDraft} />
      {conflict && <ConflictNotice onReload={() => void reload()} />}
      {error && !conflict && !(error.error === 'validation' && fieldIssues(error).length > 0) && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      {visible('name') && (
        <label className="field">
          <span>Nazwa</span>
          <input value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" />
          <FieldError error={save.error} path="name" />
        </label>
      )}
      {visible('startsAt') && (
        <>
          {(draft.moreTerms?.length ?? 0) > 0 && <p className="field-label">Termin 1</p>}
          <DaysFields value={draft} onChange={(patch) => set(patch)} />
          <FieldError error={save.error} path="startsAt" />
          <FieldError error={save.error} path="endsAt" />
          {/* several terms (owner 2026-10-21 00:44): for a role that may write them */}
          {(!me || fieldVisible(me, EVENTS_RESOURCE, 'segments')) && (
            <MoreTermsField
              first={draft}
              terms={draft.moreTerms ?? []}
              onChange={(moreTerms) => set({ moreTerms })}
              onRemoveFirst={() => {
                const [next, ...rest] = draft.moreTerms ?? [];
                if (next) set({ ...next, moreTerms: rest });
              }}
            />
          )}
          <FieldError error={save.error} path="segments" />
        </>
      )}
      {visible('contractorId') && <ClientField picked={draft.client} onPick={(client) => set({ client })} canCreate={!!me && can(me, 'contractors.contractors', 'create')} />}
      {visible('contractorId') && draft.client && <BarterWarning contractorId={draft.client.id} contractorName={draft.client.name} />}
      {visible('managerUserId') && (!me || fieldVisible(me, EVENTS_RESOURCE, 'managerUserId')) && (
        <>
          <ResponsibleField value={draft.managerUserId} currentName={base?.managerName ?? null} onChange={(managerUserId) => set({ managerUserId })} />
          <FieldError error={save.error} path="managerUserId" />
        </>
      )}
      {!base && me && fieldVisible(me, EVENTS_RESOURCE, 'viewerIds') && <ViewersFormField value={draft.viewerIds ?? [me.id]} onChange={(viewerIds) => set({ viewerIds })} />}
      {visible('location') && visible('locationAddress') && visible('locationPoint') && visible('locationApprox') ? (
        <>
          <PlaceField
            value={visiblePlace(visible('routeKm') ? draft : { ...draft, routeKm: undefined }, (f) => (base ? f in base : !me || fieldVisible(me, EVENTS_RESOURCE, f)))}
            onChange={(patch) => set(patch)}
            name={draft.name}
            startsAt={startOf(draft)}
            hint={base ? null : autoPlaceHint(autoPlace.state, placeOfDraft(draft))}
            filled={!base && isAutoFilled(autoPlace.state, placeOfDraft(draft))}
            saved={
              base && {
                point: base.locationPoint ?? null,
                distanceKm: base.distanceKm ?? null,
                distanceSource: base.distanceSource ?? null,
                travelMinutes: base.travelMinutes ?? null,
                tollsGrosze: base.tollsGrosze ?? null,
                avoidTolls: base.avoidTolls ?? false,
                autoDistance: base.autoDistance ?? null,
              }
            }
          />
          <FieldError error={save.error} path="routeKm" />
          <FieldError error={save.error} path="location" />
          <FieldError error={save.error} path="locationAddress" />
          <FieldError error={save.error} path="locationPoint" />
        </>
      ) : (
        visible('location') && (
          <label className="field">
            <span>Miejsce</span>
            <input value={draft.location} onChange={(e) => set({ location: e.target.value })} autoComplete="off" />
            <FieldError error={save.error} path="location" />
          </label>
        )
      )}
      {visible('scopes') && (
        <>
          <ScopePicker value={draft.scopes} onChange={(scopes) => set({ scopes })} custom={scopes.custom} onAdd={scopes.add} />
          <FieldError error={save.error} path="scopes" />
        </>
      )}
      {visible('systemId') && <SystemChoice value={draft.systemId} onChange={(systemId) => set({ systemId })} error={issue(save.error, 'systemId')} />}
      {visible('equipmentSource') && <EquipmentSourceField value={draft.equipmentSource} onChange={(equipmentSource) => set({ equipmentSource })} />}
      {base && visible('kind') && (
        <label className="field">
          <span>Rodzaj</span>
          <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as EventKind })} data-feature-id="events.rentals">
            {(Object.keys(EVENT_KINDS) as EventKind[]).map((k) => (
              <option key={k} value={k}>
                {EVENT_KINDS[k]}
              </option>
            ))}
          </select>
        </label>
      )}
      {visible('reservationKind') && <ReservationField value={draft.reservationKind} onChange={(reservationKind: ReservationKind) => set({ reservationKind })} />}
      {visible('reserveDaysBefore') && visible('reserveDaysAfter') && (!me || (fieldVisible(me, EVENTS_RESOURCE, 'reserveDaysBefore') && fieldVisible(me, EVENTS_RESOURCE, 'reserveDaysAfter'))) && (
        <>
          <ReserveAroundField before={draft.reserveDaysBefore ?? 0} after={draft.reserveDaysAfter ?? 0} onChange={(patch) => set(patch)} />
          <FieldError error={save.error} path="reserveDaysBefore" />
          <FieldError error={save.error} path="reserveDaysAfter" />
        </>
      )}
      {visible('needsQuote') && <QuoteSwitch value={draft.needsQuote} onChange={(needsQuote) => set({ needsQuote })} />}
      {/* „Rozliczenie” (owner 2026-11-03): financial data — only for whoever sees the field */}
      {visible('settlement') && (!me || fieldVisible(me, EVENTS_RESOURCE, 'settlement')) && (
        <label className="field" data-feature-id="events.settlement">
          <span>Rozliczenie</span>
          <select value={draft.settlement ?? 'faktura'} onChange={(e) => set({ settlement: e.target.value as Settlement })}>
            {(Object.keys(SETTLEMENT_LABELS) as Settlement[]).map((s) => (
              <option key={s} value={s}>
                {SETTLEMENT_LABELS[s]}
              </option>
            ))}
          </select>
          <FieldError error={save.error} path="settlement" />
        </label>
      )}
      {visible('notes') && (
        <label className="field">
          <span>Uwagi</span>
          <textarea rows={3} value={draft.notes} onChange={(e) => set({ notes: e.target.value })} />
        </label>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj wydarzenie'}
        </button>
        <button className="btn" type="button" onClick={() => leaveWithoutAsking(finish)}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
